import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Expression } from './Expression';
import { ExpressionGroup as ExpressionGroupType, FieldDefinition, SelectOption } from '../types';

interface ExpressionGroupProps {
  group: ExpressionGroupType;
  groupId: string;
  onUpdateExpression: (groupId: string, expressionId: string, expression: Partial<import('../types').Expression>) => void;
  onRemoveExpression: (groupId: string, expressionId: string) => void;
  onCreateExpression: (groupId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  onSwitchExpressionsOperator: (groupId: string, operator: 'AND' | 'OR') => void;
  fetchFieldCatalog: (searchTerm?: string) => Promise<FieldDefinition[]>;
  fetchResourceValues: (resourceId: string, searchTerm?: string) => Promise<SelectOption[]>;
  locale: { [key: string]: string };
  showRemoveButton?: boolean;
  showGroupOperatorToggle?: boolean;
}

export const ExpressionGroup: React.FC<ExpressionGroupProps> = ({
  group,
  groupId,
  onUpdateExpression,
  onRemoveExpression,
  onCreateExpression,
  onRemoveGroup,
  onSwitchExpressionsOperator,
  fetchFieldCatalog,
  fetchResourceValues,
  locale,
  showRemoveButton = true,
  showGroupOperatorToggle = true,
}) => {
  const expressionEntries = Object.entries(group.expressions);
  const expressionCount = expressionEntries.length;

  const handleOperatorChange = (
    _: React.MouseEvent<HTMLElement>,
    newOperator: 'AND' | 'OR' | null,
  ) => {
    if (newOperator) {
      onSwitchExpressionsOperator(groupId, newOperator);
    }
  };

  const handleAddExpression = () => {
    onCreateExpression(groupId);
  };

  const handleRemoveGroup = () => {
    onRemoveGroup(groupId);
  };

  return (
    <Paper
      id={`expression-group-${groupId}`}
      elevation={1}
      sx={{
        p: { xs: 1, md: 1.5 },
        border: '1px solid #e3f2fd',
        borderRadius: 1,
        backgroundColor: '#fafafa',
        width: '100% !important',
        maxWidth: '100% !important',
        flex: '1 1 100%',
        alignSelf: 'stretch',
        boxSizing: 'border-box',
      }}
    >
      {/* Group Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        mb: { xs: 1, md: 1.5 },
        gap: { xs: 1, sm: 0 },
        flexWrap: 'wrap'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 1, sm: 1.5 }
        }}>
          <Typography variant="body1" fontWeight="bold" color="primary" sx={{ fontSize: '0.9rem' }}> {/* Smaller font */}
            Group
          </Typography>
          
          {/* Expression Operator Toggle */}
          {showGroupOperatorToggle && expressionCount > 1 && (
            <>
              <Typography variant="caption" color="text.secondary"> {/* Changed from body2 to caption */}
                Match:
              </Typography>
              <ToggleButtonGroup
                value={group.operator}
                exclusive
                onChange={handleOperatorChange}
                size="small"
                sx={{ height: 28 }} // Reduced height
              >
                <ToggleButton value="AND" sx={{ px: 1.5, fontSize: '0.8rem' }}> {/* Reduced padding and font */}
                  <Typography variant="caption" fontWeight="bold">
                    {locale.andOperator || 'AND'}
                  </Typography>
                </ToggleButton>
                <ToggleButton value="OR" sx={{ px: 1.5, fontSize: '0.8rem' }}> {/* Reduced padding and font */}
                  <Typography variant="caption" fontWeight="bold">
                    {locale.orOperator || 'OR'}
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary"> {/* Changed from body2 to caption */}
                of these conditions
              </Typography>
            </>
          )}
        </Box>

        {/* Remove Group Button */}
        {showRemoveButton && (
          <IconButton
            onClick={handleRemoveGroup}
            size="small"
            color="error"
            sx={{ width: 28, height: 28 }} // Smaller button
            title={locale.removeButton}
          >
            <DeleteIcon sx={{ fontSize: '1rem' }} /> {/* Smaller icon */}
          </IconButton>
        )}
      </Box>

      {/* Expressions */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
        {expressionEntries.map(([expressionId, expression], index) => (
          <Box key={expressionId}>
            <Expression
              expression={expression}
              groupId={groupId}
              expressionId={expressionId}
              onUpdate={onUpdateExpression}
              onRemove={onRemoveExpression}
              fetchFieldCatalog={fetchFieldCatalog}
              fetchResourceValues={fetchResourceValues}
              locale={locale}
              showRemoveButton={expressionCount > 1}
            />
            
            {/* AND/OR Indicator between expressions */}
            {index < expressionCount - 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <Box
                  sx={{
                    backgroundColor: group.operator === 'AND' ? '#2196f3' : '#ff9800',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                  }}
                >
                  {group.operator}
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Add Expression Button */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed #ccc' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddExpression}
          size="small"
          sx={{ 
            textTransform: 'none',
            borderStyle: 'dashed',
            '&:hover': {
              borderStyle: 'solid',
            }
          }}
        >
          {group.operator === 'AND' 
            ? (locale.addAndConditionButton || 'Add AND Condition')
            : (locale.addOrConditionButton || 'Add OR Condition')
          }
        </Button>
      </Box>
    </Paper>
  );
};